import { NocfoPermissionGroups } from '@root/constants'
import { INocfoPermission, INocfoPermissionGroup } from '@root/query/constants'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaBan, FaCircle, FaEye, FaPen, FaUserShield } from 'react-icons/fa'
import styled from 'styled-components'

export interface PermissionPickerChange {
  groupId: string
  permissionIds: string[]
}

type BinaryRow = {
  key: string
  kind: 'bin'
  labelKey: string
  permissionId: string
  /** Company admin row: ban · dot · dot · shield */
  layout?: 'admin'
}

type TriRow = {
  key: string
  kind: 'tri'
  labelKey: string
  domainIds: string[]
  viewIds: string[]
  editIds: string[]
  editorId: string
}

type QuadRow = {
  key: string
  kind: 'quad'
  labelKey: string
  domainIds: string[]
  viewIds: string[]
  editIds: string[]
  adminIds: string[]
  editorId: string
  adminId: string
}

type PermissionRowDef = BinaryRow | TriRow | QuadRow

type HoverState =
  | { rowKey: string; kind: 'level'; level: number; below: boolean }
  | { rowKey: string; kind: 'na'; colName: string; below: boolean }

const PERMISSION_ROWS: PermissionRowDef[] = [
  {
    key: 'admin',
    kind: 'bin',
    layout: 'admin',
    labelKey: 'settings.people.permissionRows.admin',
    permissionId: 'admin'
  },
  {
    key: 'invoicing',
    kind: 'quad',
    labelKey: 'settings.people.permissionRows.invoicing',
    domainIds: ['invoicing_read', 'invoicing_editor', 'invoicing_admin'],
    viewIds: ['invoicing_read'],
    editIds: ['invoicing_read', 'invoicing_editor'],
    adminIds: ['invoicing_read', 'invoicing_editor', 'invoicing_admin'],
    editorId: 'invoicing_editor',
    adminId: 'invoicing_admin'
  },
  {
    key: 'expense_claims',
    kind: 'tri',
    labelKey: 'settings.people.permissionRows.expenseClaims',
    domainIds: ['expense_claims_read', 'expense_claims_editor'],
    viewIds: ['expense_claims_read'],
    editIds: ['expense_claims_read', 'expense_claims_editor'],
    editorId: 'expense_claims_editor'
  },
  {
    key: 'reporting',
    kind: 'quad',
    labelKey: 'settings.people.permissionRows.reporting',
    domainIds: ['reporting_read', 'reporting_editor', 'reporting_admin'],
    viewIds: ['reporting_read'],
    editIds: ['reporting_read', 'reporting_editor'],
    adminIds: ['reporting_read', 'reporting_editor', 'reporting_admin'],
    editorId: 'reporting_editor',
    adminId: 'reporting_admin'
  },
  {
    key: 'bookkeeping',
    kind: 'quad',
    labelKey: 'settings.people.permissionRows.bookkeeping',
    domainIds: ['bookkeeping_read', 'bookkeeping_editor', 'bookkeeping_admin'],
    viewIds: ['bookkeeping_read'],
    editIds: ['bookkeeping_read', 'bookkeeping_editor'],
    adminIds: ['bookkeeping_read', 'bookkeeping_editor', 'bookkeeping_admin'],
    editorId: 'bookkeeping_editor',
    adminId: 'bookkeeping_admin'
  },
  {
    key: 'accounts',
    kind: 'tri',
    labelKey: 'settings.people.permissionRows.accounts',
    domainIds: ['accounts_read', 'accounts_editor'],
    viewIds: ['accounts_read'],
    editIds: ['accounts_read', 'accounts_editor'],
    editorId: 'accounts_editor'
  },
  {
    key: 'files',
    kind: 'tri',
    labelKey: 'settings.people.permissionRows.files',
    domainIds: ['files_read', 'files_editor'],
    viewIds: ['files_read'],
    editIds: ['files_read', 'files_editor'],
    editorId: 'files_editor'
  },
  {
    key: 'contacts',
    kind: 'tri',
    labelKey: 'settings.people.permissionRows.contacts',
    domainIds: ['contact_read', 'contact_editor'],
    viewIds: ['contact_read'],
    editIds: ['contact_read', 'contact_editor'],
    editorId: 'contact_editor'
  },
  {
    key: 'bookkeeping_attachments',
    kind: 'bin',
    labelKey: 'settings.people.permissionRows.bookkeepingAttachments',
    permissionId: 'bookkeeping_attachment'
  },
  {
    key: 'salary',
    kind: 'bin',
    labelKey: 'settings.people.permissionRows.salary',
    permissionId: 'salary'
  },
  {
    key: 'integration_admin',
    kind: 'bin',
    labelKey: 'settings.people.permissionRows.integrations',
    permissionId: 'integration_admin'
  }
]

const COL_NAMES = ['no access', 'view', 'edit', 'admin'] as const

const rowPermissionIds = (row: PermissionRowDef): string[] =>
  row.kind === 'bin' ? [row.permissionId] : row.domainIds

const maxLevel = (row: PermissionRowDef): number => {
  if (row.kind === 'bin') return 1
  if (row.kind === 'quad') return 3
  return 2
}

const levelOf = (row: PermissionRowDef, selected: Set<string>): number => {
  if (row.kind === 'bin') return selected.has(row.permissionId) ? 1 : 0
  if (row.kind === 'quad' && selected.has(row.adminId)) return 3
  if (selected.has(row.editorId)) return 2
  if (row.viewIds.some(id => selected.has(id))) return 1
  return 0
}

const applyRowLevel = (
  selectedPermissionIds: string[],
  row: PermissionRowDef,
  level: number
): string[] => {
  const withoutDomain = selectedPermissionIds.filter(id => !rowPermissionIds(row).includes(id))

  if (row.kind === 'bin') {
    return level >= 1 ? [...withoutDomain, row.permissionId] : withoutDomain
  }

  if (row.kind === 'quad' && level >= 3) return [...withoutDomain, ...row.adminIds]
  if (level >= 2) return [...withoutDomain, ...row.editIds]
  if (level >= 1) return [...withoutDomain, ...row.viewIds]
  return withoutDomain
}

/** Slot values are permission levels; `null` is a non-interactive spacer. */
const slotsForRow = (row: PermissionRowDef): Array<number | null> => {
  if (row.kind === 'quad') return [0, 1, 2, 3]
  if (row.kind === 'tri') return [0, 1, 2]
  if (row.kind === 'bin' && row.layout === 'admin') return [0, null, null, 1]
  return [0, null, 1]
}

export const matchPermissionPreset = (
  permissionIds: string[],
  groups: INocfoPermissionGroup[]
): string => {
  const selected = new Set(permissionIds)

  for (const group of groups) {
    if (group.id === NocfoPermissionGroups.CUSTOM) continue

    const presetIds = group.permissions.map(({ id }) => id)
    if (presetIds.length === selected.size && presetIds.every(id => selected.has(id))) {
      return group.id
    }
  }

  return NocfoPermissionGroups.CUSTOM
}

interface Props {
  groups: INocfoPermissionGroup[]
  permissions: INocfoPermission[]
  selectedGroupId: string
  selectedPermissionIds: string[]
  onChange: (next: PermissionPickerChange) => void
}

export const PermissionPicker: React.FC<Props> = ({
  groups,
  permissions,
  selectedGroupId,
  selectedPermissionIds,
  onChange
}) => {
  const [t] = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<HoverState | null>(null)

  const availableIds = useMemo(() => new Set(permissions.map(({ id }) => id)), [permissions])
  const selected = useMemo(() => new Set(selectedPermissionIds), [selectedPermissionIds])
  const isAdminSelected = selected.has('admin')

  const rows = useMemo(
    () => PERMISSION_ROWS.filter(row => rowPermissionIds(row).every(id => availableIds.has(id))),
    [availableIds]
  )

  const handlePresetSelect = (groupId: string) => {
    if (groupId === selectedGroupId) return

    if (groupId === NocfoPermissionGroups.CUSTOM) {
      onChange({ groupId, permissionIds: [] })
      return
    }

    const group = groups.find(({ id }) => id === groupId)
    const permissionIds = group?.permissions.map(({ id }) => id) ?? []
    onChange({ groupId, permissionIds })
  }

  const handleRowLevel = (row: PermissionRowDef, level: number) => {
    if (isAdminSelected && row.key !== 'admin') return

    let permissionIds: string[]
    if (row.key === 'admin') {
      permissionIds = level >= 1 ? ['admin'] : []
    } else {
      permissionIds = applyRowLevel(selectedPermissionIds, row, level)
    }

    onChange({ groupId: NocfoPermissionGroups.CUSTOM, permissionIds })
  }

  const levelTitle = (row: PermissionRowDef, level: number): string => {
    if (row.kind === 'bin' && row.layout === 'admin') {
      return level === 0
        ? t('settings.people.permissionLevels.none')
        : t('settings.people.permissionLevels.fullAdmin')
    }
    if (row.kind === 'bin') {
      return level === 0
        ? t('settings.people.permissionLevels.none')
        : t('settings.people.permissionLevels.on')
    }
    if (level === 0) return t('settings.people.permissionLevels.none')
    if (level === 1) return t('settings.people.permissionLevels.view')
    if (level === 2) return t('settings.people.permissionLevels.edit')
    return t('settings.people.permissionLevels.admin')
  }

  const levelIcon = (row: PermissionRowDef, level: number) => {
    if (row.kind === 'bin' && row.layout === 'admin') {
      return level === 0 ? <FaBan key="ban" /> : <FaUserShield key="shield" />
    }
    if (row.kind === 'bin') {
      return level === 0 ? <FaBan key="ban" /> : <FaPen key="pen" />
    }
    if (level === 0) return <FaBan key="ban" />
    if (level === 1) return <FaEye key="eye" />
    if (level === 2) return <FaPen key="pen" />
    return <FaUserShield key="shield" />
  }

  const tipCopy = (row: PermissionRowDef, hoverState: HoverState) => {
    const label = t(row.labelKey)
    if (hoverState.kind === 'na') {
      return {
        title: t('settings.people.permissionTip.notAvailable'),
        text: t('settings.people.permissionTip.levelNotSupported', {
          label,
          level: hoverState.colName
        })
      }
    }
    if (hoverState.level === 0) {
      return {
        title: `${label} · ${levelTitle(row, 0)}`,
        text: t('settings.people.permissionDescriptions.none', {
          label: label.toLowerCase()
        })
      }
    }
    return {
      title: `${label} · ${levelTitle(row, hoverState.level)}`,
      text: t(`settings.people.permissionDescriptions.${row.key}.${hoverState.level}`)
    }
  }

  const onSegmentHover = (
    target: HTMLElement,
    rowKey: string,
    next: { kind: 'level'; level: number } | { kind: 'na'; colName: string }
  ) => {
    const rowEl = target.closest('[data-perm-row]')
    const scroller = scrollerRef.current
    let below = false
    if (rowEl && scroller) {
      below = rowEl.getBoundingClientRect().top - scroller.getBoundingClientRect().top < 150
    }
    setHover({ rowKey, below, ...next })
  }

  return (
    <StyledPicker ref={scrollerRef}>
      <PresetPills>
        {groups.map(group => {
          const isActive = group.id === selectedGroupId
          return (
            <PresetPill
              key={group.id}
              type="button"
              $isActive={isActive}
              onClick={() => handlePresetSelect(group.id)}
            >
              {group.name}
            </PresetPill>
          )
        })}
      </PresetPills>

      <Rows>
        {rows.map(row => {
          const lockedByAdmin = isAdminSelected && row.key !== 'admin'
          const level = lockedByAdmin ? maxLevel(row) : levelOf(row, selected)
          const slots = slotsForRow(row)
          const rowHover = hover?.rowKey === row.key ? hover : null
          const tip = rowHover ? tipCopy(row, rowHover) : null

          return (
            <PermissionRow key={row.key} data-perm-row={row.key}>
              <RowLabel>{t(row.labelKey)}</RowLabel>
              <SegmentTrack>
                <Segmented $columns={slots.length} $disabled={lockedByAdmin}>
                  {slots.map((slotLevel, i) => {
                    if (slotLevel === null) {
                      return (
                        <SpacerSegment
                          key={`na-${i}`}
                          type="button"
                          aria-label={t('settings.people.permissionTip.levelNotSupported', {
                            label: t(row.labelKey),
                            level: COL_NAMES[i]
                          })}
                          tabIndex={-1}
                          onMouseEnter={e =>
                            onSegmentHover(e.currentTarget, row.key, {
                              kind: 'na',
                              colName: COL_NAMES[i]
                            })
                          }
                          onMouseLeave={() => setHover(null)}
                          onFocus={e =>
                            onSegmentHover(e.currentTarget, row.key, {
                              kind: 'na',
                              colName: COL_NAMES[i]
                            })
                          }
                          onBlur={() => setHover(null)}
                        >
                          <FaCircle />
                        </SpacerSegment>
                      )
                    }

                    const isActive = slotLevel === level
                    const title = levelTitle(row, slotLevel)
                    return (
                      <Segment
                        key={slotLevel}
                        type="button"
                        title={title}
                        aria-label={`${t(row.labelKey)}: ${title}`}
                        aria-pressed={isActive}
                        $active={isActive}
                        $isNone={slotLevel === 0}
                        $disabled={lockedByAdmin}
                        disabled={lockedByAdmin}
                        onClick={() => handleRowLevel(row, slotLevel)}
                        onMouseEnter={e =>
                          onSegmentHover(e.currentTarget, row.key, {
                            kind: 'level',
                            level: slotLevel
                          })
                        }
                        onMouseLeave={() => setHover(null)}
                      >
                        {levelIcon(row, slotLevel)}
                      </Segment>
                    )
                  })}
                </Segmented>
              </SegmentTrack>

              {tip && rowHover && (
                <Tip $below={rowHover.below}>
                  <TipTitle>{tip.title}</TipTitle>
                  <TipText>{tip.text}</TipText>
                </Tip>
              )}
            </PermissionRow>
          )
        })}
      </Rows>
    </StyledPicker>
  )
}

const StyledPicker = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

const PresetPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const PresetPill = styled.button<{ $isActive: boolean }>`
  padding: 0.45rem 0.9rem;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $isActive }) => ($isActive ? 'transparent' : theme.colors.neutralGray)};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.neutralWhite};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.neutralWhite : theme.colors.neutralBlack};

  &:focus,
  &:focus-visible {
    outline: none;
  }
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`

const PermissionRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: 0.7rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const SegmentTrack = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 152px;
  flex-shrink: 0;
`

const Segmented = styled.div<{ $columns: number; $disabled?: boolean }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 32px);
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
`

const Segment = styled.button<{
  $active: boolean
  $isNone: boolean
  $disabled?: boolean
}>`
  width: 32px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 7px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  font-size: 0.75rem;
  background: ${({ theme, $active, $isNone }) => {
    if (!$active) return 'transparent'
    return $isNone ? theme.colors.iconGray : theme.colors.primary
  }};
  color: ${({ theme, $active }) => ($active ? theme.colors.neutralWhite : theme.colors.iconGray)};

  &:focus,
  &:focus-visible {
    outline: none;
  }

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }

  &:disabled {
    opacity: ${({ $active }) => ($active ? 0.85 : 0.45)};
  }
`

const SpacerSegment = styled.button`
  width: 32px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 7px;
  background: transparent;
  cursor: default;
  color: ${({ theme }) => theme.colors.metalGray};
  opacity: 0.18;
  font-size: 0.22em;
  padding: 0;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  svg {
    width: 0.35rem;
    height: 0.35rem;
  }
`

const Tip = styled.div<{ $below: boolean }>`
  position: absolute;
  right: 0;
  z-index: 20;
  ${({ $below }) => ($below ? 'top: calc(100% - 0.35rem);' : 'bottom: calc(100% - 0.35rem);')}
  width: 320px;
  max-width: min(320px, 90vw);
  background: ${({ theme }) => theme.colors.neutralBlack};
  color: ${({ theme }) => theme.colors.neutralWhite};
  padding: 0.6rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: ${({ theme }) => theme.shadow.md};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.45;
  pointer-events: none;
`

const TipTitle = styled.div`
  font-weight: 700;
  margin-bottom: 0.2rem;
`

const TipText = styled.div`
  opacity: 0.85;
`
